// import { useCallback, useRef } from 'react';
// import { ReturnOrderLineType, ReturnSlip } from 'returnCenter-api';

// type CellInputElement = HTMLInputElement | HTMLSelectElement | HTMLButtonElement | HTMLDivElement | null;

import { SelectInstance } from 'react-select';
import { SelectOption } from 'types/Option';

import { MutableRefObject, useCallback, useRef } from 'react';
import { ReturnOrderLineType, ReturnSlip } from 'returnCenter-api';
// import { SelectInstance } from 'react-select';
// import { SelectOption } from 'types/Option';

export type CellInputElement =
    | HTMLInputElement
    | HTMLSelectElement
    | HTMLButtonElement
    | HTMLDivElement
    | SelectInstance<unknown, boolean>;

export type CellInputRefs = MutableRefObject<Record<string, CellInputElement | null>>;

// type CellInputRefs = {
//     [key: string]: CellInputElement;
// };

type Direction = 'up' | 'down' | 'left' | 'right';

interface UseKeyboardNavigationOptions {
    returnSlip: ReturnSlip | null | undefined;
}

export const useKeyboardNavigation = ({ returnSlip }: UseKeyboardNavigationOptions) => {
    // const cellInputRefs = useRef<CellInputRefs>({});

    const cellInputRefs = useRef<Record<string, CellInputElement | null>>({});

    const findNextEnabledCell = useCallback(
        (
            prefix: string,
            startRow: number,
            startCol: number,
            direction: Direction,
            maxAttempts: number = 10,
        ): string | null => {
            let row = startRow;
            let col = startCol;

            for (let i = 0; i < maxAttempts; i++) {
                if (direction === 'right') col++;
                else if (direction === 'left') col--;
                else if (direction === 'down') row++;
                else if (direction === 'up') row--;

                if (col < 0 || row < 0) return null;

                const key = `${prefix}${row}-${col}`;
                const element = cellInputRefs.current[key];

                if (element) {
                    const isDisabled =
                        ('disabled' in element &&
                            (element as HTMLInputElement | HTMLSelectElement | HTMLButtonElement).disabled) ||
                        ('props' in element && (element as any).props?.isDisabled);

                    if (!isDisabled) {
                        return key;
                    }
                    // Continue searching in the same direction for left/right
                    if (direction === 'left' || direction === 'right') {
                        continue;
                    }
                }

                // For up/down, if no element found at this position, stop
                if (direction === 'up' || direction === 'down') {
                    if (!element) return null;
                }
            }
            return null;
        },
        [],
    );

    // Helper: find the max row index for invoice charges for a given order number
    const findInvoiceChargeMaxRow = useCallback((orderNumber: string): number => {
        const prefix = `invoiceCharges-${orderNumber}-`;
        let maxRow = -1;
        for (const key of Object.keys(cellInputRefs.current)) {
            if (key.startsWith(prefix)) {
                const parts = key.slice(prefix.length).split('-');
                const row = parseInt(parts[0], 10);
                if (!isNaN(row) && row > maxRow) maxRow = row;
            }
        }
        return maxRow;
    }, []);

    // Helper: try to focus the first enabled cell in a range of columns at a given row/prefix
    const tryFocusCell = useCallback(
        (
            prefix: string,
            row: number,
            startCol: number,
            endCol: number,
            direction: 'left' | 'right',
            e: React.KeyboardEvent,
        ): boolean => {
            const step = direction === 'left' ? -1 : 1;
            for (let col = startCol; direction === 'left' ? col >= endCol : col <= endCol; col += step) {
                const key = `${prefix}${row}-${col}`;
                const element = cellInputRefs.current[key];
                if (element) {
                    const isDisabled =
                        ('disabled' in element &&
                            (element as HTMLInputElement | HTMLSelectElement | HTMLButtonElement).disabled) ||
                        ('props' in element && (element as any).props?.isDisabled);
                    if (!isDisabled) {
                        element.focus();
                        e.preventDefault();
                        return true;
                    }
                }
            }
            return false;
        },
        [],
    );

    const handleArrowKeyDown = useCallback(
        (e: React.KeyboardEvent, itemId: string | number, rowIndex: number, colIndex: number, orderId?: string) => {
            const prefix = orderId ? `${orderId}-` : '';
            let direction: Direction | null = null;

            if (e.key === 'ArrowRight') direction = 'right';
            else if (e.key === 'ArrowLeft') direction = 'left';
            else if (e.key === 'ArrowDown') direction = 'down';
            else if (e.key === 'ArrowUp') direction = 'up';

            if (!direction) return;

            // Try to find next enabled cell in the same section
            const nextKey = findNextEnabledCell(prefix, rowIndex, colIndex, direction);

            if (nextKey && cellInputRefs.current[nextKey]) {
                cellInputRefs.current[nextKey]?.focus();
                e.preventDefault();
                return;
            }

            // Handle cross-section navigation
            const isInvoiceCharges = orderId?.startsWith('invoiceCharges-');
            const invoiceOrderNumber = isInvoiceCharges ? orderId!.slice('invoiceCharges-'.length) : null;

            if (isInvoiceCharges && invoiceOrderNumber) {
                // Navigation from invoice charges section
                const orderGroups = returnSlip?.returnItems ?? [];
                const currentOrderIndex = orderGroups.findIndex((g) => g.orderNumber === invoiceOrderNumber);

                if (direction === 'up' && rowIndex === 0) {
                    // Transition B: invoice charges row 0 → up to same order's main table last row
                    if (currentOrderIndex >= 0) {
                        const orderGroup = orderGroups[currentOrderIndex];
                        const totalRows =
                            orderGroup?.orderLines
                                ?.filter((ol) => ol.type !== ReturnOrderLineType.InvoiceCharge)
                                .reduce((sum, line) => sum + (line.returnItems?.length ?? 0), 0) ?? 0;
                        const lastRowIndex = totalRows - 1;
                        if (lastRowIndex >= 0) {
                            tryFocusCell(`${invoiceOrderNumber}-`, lastRowIndex, colIndex, 0, 'left', e);
                        }
                    }
                    return;
                }

                if (direction === 'down') {
                    // Transition C: invoice charges last row → down to next order or additionalCosts
                    if (currentOrderIndex >= 0 && currentOrderIndex < orderGroups.length - 1) {
                        const nextOrderId = orderGroups[currentOrderIndex + 1]?.orderNumber;
                        if (nextOrderId) {
                            tryFocusCell(`${nextOrderId}-`, 0, colIndex, 0, 'left', e);
                        }
                    } else {
                        // Last order's invoice charges → additionalCosts
                        const mappedColIndex = colIndex >= 2 && colIndex <= 4 ? colIndex : 2;
                        tryFocusCell('additionalCosts-', 0, mappedColIndex, 5, 'right', e);
                    }
                    return;
                }
            } else if (orderId === 'additionalCosts') {
                // Handle navigation from additionalCosts
                if (direction === 'up' && rowIndex === 0) {
                    // Transition E: additionalCosts row 0 → up to last order's invoice charges (if any), else last order table
                    const orderGroups = returnSlip?.returnItems ?? [];
                    if (orderGroups.length > 0) {
                        const lastOrder = orderGroups[orderGroups.length - 1];
                        const lastOrderId = lastOrder?.orderNumber;
                        if (lastOrderId) {
                            // Try invoice charges first
                            const invoiceMaxRow = findInvoiceChargeMaxRow(lastOrderId);
                            if (invoiceMaxRow >= 0) {
                                if (
                                    tryFocusCell(
                                        `invoiceCharges-${lastOrderId}-`,
                                        invoiceMaxRow,
                                        colIndex,
                                        0,
                                        'left',
                                        e,
                                    )
                                ) {
                                    return;
                                }
                            }

                            // Fall through to last order table last row
                            const totalRows =
                                lastOrder.orderLines
                                    ?.filter((ol) => ol.type !== ReturnOrderLineType.InvoiceCharge)
                                    .reduce((sum, line) => sum + (line.returnItems?.length ?? 0), 0) ?? 0;
                            const lastRowIndex = totalRows - 1;
                            const mappedColIndex = colIndex <= 4 ? colIndex + 1 : 7;
                            tryFocusCell(`${lastOrderId}-`, lastRowIndex, mappedColIndex, 0, 'left', e);
                        }
                    }
                }
            } else if (orderId && (direction === 'down' || direction === 'up')) {
                // Navigation from order table
                const orderGroups = returnSlip?.returnItems ?? [];
                const currentOrderIndex = orderGroups.findIndex((g) => g.orderNumber === orderId);

                if (direction === 'down') {
                    // Transition A: order table last row → down to invoice charges (if any), else next order / additionalCosts
                    const invoiceMaxRow = findInvoiceChargeMaxRow(orderId);
                    if (invoiceMaxRow >= 0) {
                        if (tryFocusCell(`invoiceCharges-${orderId}-`, 0, colIndex, 0, 'left', e)) {
                            return;
                        }
                    }

                    if (currentOrderIndex < orderGroups.length - 1) {
                        // Navigate to first row of next order
                        const nextOrderId = orderGroups[currentOrderIndex + 1]?.orderNumber;
                        if (nextOrderId) {
                            tryFocusCell(`${nextOrderId}-`, 0, colIndex, 0, 'left', e);
                        }
                    } else {
                        // Last order - try to navigate to additionalCosts
                        const mappedColIndex = colIndex >= 2 && colIndex <= 4 ? colIndex : 2;
                        tryFocusCell('additionalCosts-', 0, mappedColIndex, 5, 'right', e);
                    }
                } else if (direction === 'up' && rowIndex === 0) {
                    // Transition D: order table row 0 → up to previous order's invoice charges (if any), else previous order table
                    if (currentOrderIndex > 0) {
                        const prevOrderId = orderGroups[currentOrderIndex - 1]?.orderNumber;
                        const prevOrderGroup = orderGroups[currentOrderIndex - 1];
                        if (prevOrderId && prevOrderGroup) {
                            // Try previous order's invoice charges first
                            const invoiceMaxRow = findInvoiceChargeMaxRow(prevOrderId);
                            if (invoiceMaxRow >= 0) {
                                if (
                                    tryFocusCell(
                                        `invoiceCharges-${prevOrderId}-`,
                                        invoiceMaxRow,
                                        colIndex,
                                        0,
                                        'left',
                                        e,
                                    )
                                ) {
                                    return;
                                }
                            }

                            // Fall through to previous order table last row
                            const totalRows =
                                prevOrderGroup.orderLines
                                    ?.filter((ol) => ol.type !== ReturnOrderLineType.InvoiceCharge)
                                    .reduce((sum, line) => sum + (line.returnItems?.length ?? 0), 0) ?? 0;
                            const lastRowIndex = totalRows - 1;
                            tryFocusCell(`${prevOrderId}-`, lastRowIndex, colIndex, 0, 'left', e);
                        }
                    }
                }
            }
        },
        [returnSlip, findNextEnabledCell, findInvoiceChargeMaxRow, tryFocusCell],
    );

    return {
        cellInputRefs,
        handleArrowKeyDown,
    };
};

export default useKeyboardNavigation;
